import type { ApplogForInsertOptionalAgent, CidString, EntityID } from '@wovin/core/applog'
import type { Thread, ThreadOnlyCurrentNoDeleted, ThreadWithoutFilters, WriteableThread } from '@wovin/core/thread'
import type { AgentStateClass } from './agent/AgentState'
import type { RelationModelDef } from './Relations'
import type { AppSettingsVM } from './VMs/AppSettingsVM'
import type { RelBuilder } from './VMs/RelationVM'
import type { TiptapContent } from './VMs/TypeMap'
import { generateJSON } from '@tiptap/core'
import { renderToMarkdown } from '@tiptap/static-renderer/pm/markdown'
import { dateNowIso, EntityID_LENGTH, mintNewEntity } from '@wovin/core/applog'
import { queryAndMap } from '@wovin/core/query'
import { DefaultFalse, DefaultTrue } from '@wovin/utils'
import { Logger } from 'besonders-logger'
import { XMLBuilder } from 'fast-xml-parser'
import uniq from 'lodash-es/uniq'
import stringify from 'safe-stable-stringify'
import { baseExtensions, htmlToSerializedTiptap } from '../components/TipTapExtentions'
import { useCurrentThread, useRawThread, useRelationVM } from '../ui/reactive'
import { focusBlockAsInput } from '../ui/utils-ui'
import { insertApplogs, insertApplogsInAppDB } from './ApplogDB'
import { compareBlockContent, serializeTiptapToVl } from './block-utils-nowin'
import { BLOCK_DEF, ENTITY_DEF, REL_DEF } from './data-types'
import { getDerivedKids } from './derived-relations-core'
import { BlockVM, useBlk } from './VMs/BlockVM'
import { REL, RelationVM, useRel } from './VMs/RelationVM'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO) // eslint-disable-line unused-imports/no-unused-vars

export const notDeletedFilter = en => !en.isDeleted
// const debounceWait = 1500

export function persistBlockContent(this: any, thread: Thread, blockID: EntityID, newContent: TiptapContent, pv: CidString = null) {
	const currentContent = queryAndMap(useCurrentThread(thread), { en: blockID, at: 'content' }, 'vl')[0] ?? null
	// if (currentContent === null) return // not loaded yet
	if (!compareBlockContent(newContent, currentContent)) {
		DEBUG('set content', { currentContent, newContent, blockID })
		insertApplogs(thread, [{
			en: blockID,
			at: BLOCK_DEF.content,
			vl: serializeTiptapToVl(newContent),
			pv,
		}] /* , { focus: true, id: blockID, pos } */) // TODO: which thread?
	} else {
		VERBOSE('set content noop', { newContent, blockVM: this })
	}
}

// export function persistBlockisDeleted(thread: Thread, blockID: EntityID, newisDeleted: boolean) {
// 	const currentisDeleted = untracked(() => withThread(thread, () => useBlockAt(blockID, 'isDeleted').get())) // ? not needed I think
// 	// if (currentisDeleted === null) return // not loaded yet
// 	if (newisDeleted != currentisDeleted) {
// 		DEBUG('set isDeleted', { currentisDeleted, newisDeleted, blockID })
// 		insertApplogs(thread, [{ en: blockID, at: 'block/isDeleted', vl: newisDeleted }])
// 	} else VERBOSE('set isDeleted noop', { newisDeleted, blockVM: this })
// }

export function htmlToTiptap(html: string) {
	return generateJSON(html, baseExtensions) satisfies TiptapContent
}

export function outdentBlk(thread: Thread, relation: RelationModelDef, grandParentID = null) {
	// const appLogDB = getApplogDB()
	if (!relation) {
		return WARN('outdent not possible: no relation (derived placement?)', { relation })
	}
	const blockID = relation.block
	const previousParent = relation.childOf
	if (!grandParentID) {
		DEBUG('outdenting up to a root node', { relation })
	}

	DEBUG('move block for outdent', { thread, relation, previousParent, grandParentID })
	moveBlock({ thread, relationID: relation.en, blockID, asChildOf: grandParentID, after: previousParent })
}

export function indentBlk(thread: Thread, relation: RelationModelDef) {
	// check if its possible to indent (i am not the first or only kid -  i must have childof and after)
	if (!relation?.after || !relation?.childOf) {
		return WARN('indent not possible', { relation })
	}
	// TODO deal with it when more than one kid has the same after (actually legit if the kids were created by differnt agents)
	const moveOptions = { thread, relationID: relation.en, blockID: relation.block, asChildOf: relation.after }
	DEBUG('indent via', moveOptions)
	moveBlock(moveOptions)
}

const exampleOPMLfromWorkflowy = `
<?xml version="1.0"?>
<opml version="2.0">
  <head>
    <ownerEmail>
      gotjoshua@gmail.com
    </ownerEmail>
  </head>
  <body>
    <outline text="Residence">
      <outline text="2,3 " />
      <outline text="Try to make appointments">
        <outline text="Emails , auto response, story" />
      </outline>
      <outline text="Volunteer paper from tamera" />
      <outline text="" />
    </outline>
  </body>
</opml>`

/**
 * Materialize a real relation for a derived placement (see
 * data/derived-relations.ts): the block becomes a real kid of the anchor,
 * inserted at the bottom (where derived rows render). The derived placement
 * disappears automatically — real relations trump the derivation gate.
 * Returns the new relation's entity ID.
 */
export function adoptDerivedPlacement(thread: ThreadOnlyCurrentNoDeleted, blockID: EntityID, anchorID: EntityID): EntityID {
	DEBUG(`[adoptDerivedPlacement]`, { blockID, anchorID })
	return insertBlockInRelChain(thread, blockID, anchorID, null, undefined, { bottom: true })
}

/**
 * Kids as rendered: real kid VMs + derived placements (visible-consistent
 * exports — see data/derived-relations-core.ts).
 */
function visibleKidVMs(bVM: BlockVM, rawRS: Thread): BlockVM[] {
	const derived = getDerivedKids(useCurrentThread(rawRS), bVM.en).map(id => BlockVM.get(id, rawRS))
	return derived.length ? [...bVM.kidVMs, ...derived] : (bVM.kidVMs as BlockVM[])
}

export function getRecursiveKidsOPML(rootBlockID, includeRoot = DefaultTrue, rawRS = useRawThread()) {
	const rootVM = BlockVM.get(rootBlockID, rawRS)
	const opmlObj = {
		'?xml': { _version: '1.0' },
		'opml': {
			// 'head': {
			// 	'ownerEmail': 'gotjoshua@gmail.com',
			// },
			body: { outline: {} },
			_version: '2.0',
		},
	}
	const outlineNodeFromBVM = (bVM: BlockVM) => {
		return {
			_text: bVM.contentPlaintext,
			_tiptap: stringify(bVM.content),
		}
	}
	const addContent = (bVM: BlockVM, objToMutate) => {
		Object.assign(objToMutate, outlineNodeFromBVM(bVM))
		const kids = visibleKidVMs(bVM, rawRS)
		if (kids.length) {
			objToMutate.outline = kids.map((eachKidKidVM) => {
				const eachKidOutlineNode = outlineNodeFromBVM(eachKidKidVM)
				if (visibleKidVMs(eachKidKidVM, rawRS).length) {
					addContent(eachKidKidVM, eachKidOutlineNode)
				} else {
					VERBOSE(eachKidKidVM.en, 'has no kids', eachKidKidVM.contentPlaintext)
				}
				return eachKidOutlineNode
			})

			DEBUG('addContent', { opmlObj, bVM })
		}
	}
	addContent(rootVM, opmlObj.opml.body.outline)
	const opml = new XMLBuilder({
		ignoreAttributes: false,
		attributeNamePrefix: '_',
		format: true,
	})
	const opmlResult = opml.build(opmlObj)
	DEBUG('opml', { opml, opmlResult, opmlObj })
	return opmlResult
}

/**
 * Convert Tiptap JSON content to markdown with formatting preserved
 */
export function tiptapToMarkdown(tiptapContent: TiptapContent): string {
	if (!tiptapContent) return ''
	return renderToMarkdown({
		extensions: baseExtensions,
		content: tiptapContent,
	}).trim()
}

export function getRecursiveKidsJSON(rootBlockID, format: 'md' | 'html', rawRS = useRawThread()) {
	const rootVM = BlockVM.get(rootBlockID, rawRS)

	// For markdown: build list directly to avoid double-escaping
	if (format === 'md') {
		const lines: string[] = []
		const addMarkdownContent = (bVM: BlockVM, indent: number) => {
			const prefix = `${'  '.repeat(indent)}- `
			lines.push(prefix + tiptapToMarkdown(bVM.content))
			for (const kidVM of visibleKidVMs(bVM, rawRS)) {
				addMarkdownContent(kidVM, indent + 1)
			}
		}
		addMarkdownContent(rootVM, 0)
		return lines.join('\n')
	}

	// For HTML: use XML builder approach
	const rootUL = []
	const baseObj = { ul: rootUL }

	const addContent = (bVM: BlockVM, objToMutate) => {
		const thisRootLI: any[] = [{
			'#text': bVM.contentPlaintext,
		}]
		objToMutate.push({ li: thisRootLI })
		const kids = visibleKidVMs(bVM, rawRS)
		if (kids.length) {
			const kidsLIs = []
			const kidsUL = { ul: kidsLIs }
			for (const eachBlkVM of kids) {
				if (visibleKidVMs(eachBlkVM, rawRS).length) {
					addContent(eachBlkVM, kidsLIs)
				} else {
					kidsLIs.push({ li: eachBlkVM.contentPlaintext })
				}
			}
			thisRootLI.push(kidsUL)
		}
		VERBOSE({ baseObj, bVM })
	}
	addContent(rootVM, rootUL)
	const opml = new XMLBuilder({
		ignoreAttributes: false,
		attributeNamePrefix: '_',
		format: true,
		// preserveOrder: true,
		oneListGroup: true,
	})
	const htmlResult = opml.build(baseObj)
	DEBUG('copy node content', rootBlockID, { opml, htmlResult, baseObj })
	return htmlResult
}

interface AddBlockRelationOptions {
	thread: Thread
	asChildOf: EntityID | null
	after?: EntityID | null
	blockID: EntityID | null
	focus?: boolean
	bottom?: boolean
}
type MoveBlockOptions = AddBlockRelationOptions & { relationID: EntityID | null }

export async function moveBlock({ thread, asChildOf, after, blockID, relationID, focus = true, bottom = true }: MoveBlockOptions) {
	// Fix sibling `after` chains before removing this block from its current position
	// This updates any siblings that had `after: blockID` to point to what this block was after
	if (relationID) {
		removeBlockFromRelChain(thread as ThreadOnlyCurrentNoDeleted, blockID, relationID)
	}

	// Insert block at new location - this also updates siblings' `after` chains at destination
	insertBlockInRelChain(thread as ThreadOnlyCurrentNoDeleted, blockID, asChildOf, after, undefined, { bottom })

	// Delete the old relation
	if (relationID) {
		insertApplogsInAppDB(getDeleteRelationAtoms(relationID))
	}

	maybeFocusAfterInsert({ inputFocus: focus, id: blockID, end: true })
}
export function getAddBlockRelationLogs({ thread, asChildOf, after, blockID, focus = true, bottom = true }: AddBlockRelationOptions) {
	// Note: This creates a relation without updating sibling chains.
	// For moves, use insertBlockInRelChain instead which handles chain updates.
	// Mints a NEW relation soul (ts in the seed → each add is a distinct relation occurrence).
	// Moves reuse the existing relation id via insertBlockInRelChain, not this path.
	const newRelationID = mintNewEntity([asChildOf, blockID, dateNowIso()], EntityID_LENGTH)
	const blockToMoveVM = BlockVM.get(blockID, thread)
	const newParentBlockVM = asChildOf && BlockVM.get(asChildOf, thread)
	let newApplogs: ApplogForInsertOptionalAgent[]
	if (!after) {
		if (bottom) {
			const currentBottomBlockID = after = (newParentBlockVM.kidRelations.slice(-1)[0] ?? {}).block ?? null // explicit null if there are no kids to be at the bottom of
			VERBOSE('using bottom as after', { currentBottomBlockID, newParentBlockVM, blockToMoveVM })
		} else {
			WARN('a bit odd to explicitly disable bottom and not pass after')
		}
	}
	newApplogs = getAppLogsForNewRelation({ newRelationID, asChildOf, after, blockID })
	VERBOSE('getAddBlockRelationLogs returning', { newApplogs })
	return newApplogs
}
export function getAppLogsForNewRelation({ asChildOf, after, newRelationID, blockID }: {
	newRelationID: EntityID | null
	asChildOf: EntityID | null
	blockID: EntityID | null
	after?: EntityID | null
}) {
	// Note: This creates applogs without updating sibling chains.
	// For moves, use insertBlockInRelChain instead which handles chain updates.
	const newApplogs = []
	if (asChildOf) {
		newApplogs.push(
			{ en: newRelationID, at: 'relation/childOf', vl: asChildOf },
			{ en: newRelationID, at: 'relation/block', vl: blockID },
			{ en: newRelationID, at: 'relation/after', vl: after ?? null },
		)
		// TODO get parentRelation
		// TODO if(!parentRelation.isExpanded) {newApplogs.push({ en: parentRelation.id, at: REL_DEF.isExpanded, vl: true })}
	}
	return newApplogs as ApplogForInsertOptionalAgent[]
}
export interface AddBlockOpts {
	thread: ThreadOnlyCurrentNoDeleted
	asChildOf: EntityID | null
	after?: EntityID | null
	content?: string
	inputFocus?: boolean
}
export const addBlock = function addBlock({
	thread,
	asChildOf,
	after = null,
	content = '', // TODO: TIPTAP_EMPTY?
	inputFocus = false,
}: AddBlockOpts) {
	DEBUG('Adding block', { asChildOf, after, content, focus: inputFocus })

	const blockBuilder = BlockVM
		.buildNew({ content: content as any as TiptapContent }) // HACK: content type?!
		.ensureEn() // TODO: add salt (e.g. asChildOf)?
	// const relBuilder = RelationVM.buildNew({
	// 	block: blockBuilder.en,
	// 	childOf: asChildOf,
	// 	after: after || null,
	// })

	// 	if (focus)
	// relBuilder.update({isExpanded:true}) // ? default isExpanded is true anyways, sure we want it explicit
	insertApplogs(thread, [
		...blockBuilder.build(),
		// ...relBuilder.build(),
	])
	let newRelID
	// TODO make into transaction style and offer option to return applogs without commit
	if (asChildOf) {
		newRelID = insertBlockInRelChain(thread, blockBuilder.en, asChildOf, after ?? null)
	}

	maybeFocusAfterInsert({ inputFocus, id: blockBuilder.en, end: false })
	DEBUG('Block created:', blockBuilder.en, { newRelID })
	return blockBuilder.en
}
function insertAndMaybeFocus(
	thread: WriteableThread,
	newApplogs: ApplogForInsertOptionalAgent[],
	{ id = '', inputFocus = DefaultFalse, start = DefaultFalse, end = DefaultFalse, pos = 0 } = {},
) {
	const _insertResult = insertApplogs(thread, newApplogs)
	maybeFocusAfterInsert({ inputFocus, id, end, pos, start })
}
function maybeFocusAfterInsert(
	{ id = '', inputFocus = DefaultFalse, start = DefaultFalse, end = DefaultFalse, pos = 0 } = {},
) {
	if (!id) return
	if (inputFocus) {
		setTimeout(() => {
			focusBlockAsInput({ id, end, pos, start })
		})
	}
}

export function getDeleteRelationAtoms(en: EntityID) {
	return [{ at: 'isDeleted', en, vl: true }]
}
// export function deleteRelation(en: EntityID) {
// 	const atoms = getDeleteRelationAtoms(en)
// 	VERBOSE('deleting relation', en, { atoms })
// 	insertApplogsIDB(atoms)
// }
export function removeBlockRelAndMaybeDelete(thread: Thread, blockID: EntityID, relationID: EntityID | null) {
	const blockVM = useBlk(blockID, thread)
	const appLogsToInsert = relationID ? getDeleteRelationAtoms(relationID) : []
	if (!relationID || blockVM.parentRelations.length <= 1) {
		appLogsToInsert.push({ en: blockID, at: 'isDeleted', vl: true })
	}
	DEBUG(`[removeBlockRelAndMaybeDelete]`, blockID, { relationID, parents: blockVM.parentRelations })
	if (!appLogsToInsert.length) {
		WARN('somethings off, removeBlockRelAndMaybeDelete did nothing')
	} else {
		insertApplogsInAppDB(appLogsToInsert)
	}
}

export function deleteAndReplaceBlock(relationID: EntityID, newBlockID: string, isMirror = false) {
	// TODO BUG What if it is a "top level" note without a relation
	const relation = useRelationVM(relationID)
	// TODO what if its not the only reference of the block??
	// i think only after and isExpanded ought to be changable...
	// otherwise (i thought we already agreed that) deleting the relation and making a new one is better
	if (relation) {
		insertApplogsInAppDB([
			{ en: relation.block, at: ENTITY_DEF.isDeleted, vl: true }, // delete block
			{ en: relation.en, at: REL_DEF.block, vl: newBlockID }, // update relation to point at pasted block
			isMirror ? { en: relation.en, at: REL_DEF.isMirror, vl: true } : null,
		])
	}
}

export function removeBlockFromRelChain(thread: ThreadOnlyCurrentNoDeleted, blockID: EntityID, relationToParentID: EntityID) {
	DEBUG(`[removeBlockFromRelChain]`, { blockID, relationToParentID })
	const relationToParent = useRel(relationToParentID, thread)
	const wasAfterRelID = relationToParent.after
	const relationsAfterMe = queryAndMap(thread, [
		{ en: '?relID', at: REL.childOf, vl: relationToParent.childOf }, // relations of siblings
		{ en: '?relID', at: REL_DEF.after, vl: blockID }, // that are 'after' this block
	], 'relID')
	DEBUG(`[removeBlockFromRelChain] data:`, { wasAfterRelID, relationsAfterMe })
	if (relationsAfterMe.length) {
		insertApplogsInAppDB([
			// update relations referring to me
			...uniq(relationsAfterMe).map(relID => (
				{ en: relID, at: REL_DEF.after, vl: wasAfterRelID }
			)),
		])
	}
}

export function insertBlockInRelChain(
	thread: ThreadOnlyCurrentNoDeleted,
	blockID: EntityID,
	parentID: EntityID,
	after: EntityID | null,
	relationToParentID?: EntityID,
	{ bottom = false }: { bottom?: boolean } = {},
) {
	// Compute `after` for bottom insertion
	if (!after && bottom && parentID) {
		const parentBlockVM = BlockVM.get(parentID, thread)
		after = (parentBlockVM.kidRelations.slice(-1)[0] ?? {}).block ?? null
	}
	DEBUG(`[insertBlockInRelChain]`, { blockID, parentID, after, relationToParentID })

	let relBuilder: InstanceType<typeof RelBuilder>
	if (relationToParentID) {
		const relToParentVM = useRel(relationToParentID, thread)
		relBuilder = relToParentVM.buildUpdate()
		if (relToParentVM.childOf !== parentID) {
			relBuilder.update({ childOf: parentID })
		}
	} else {
		relBuilder = RelationVM.buildNew({ block: blockID, childOf: parentID })
	}
	relBuilder.update({ after })
	relationToParentID = relBuilder.commit(thread).en

	// update relations to refer to me
	const relationsAfterTarget = queryAndMap(thread, [
		{ en: '?rel', at: REL_DEF.childOf, vl: parentID }, // relations of siblings
		{ en: '?rel', at: REL_DEF.after, vl: after }, // that are 'after' who we want to be after
	], 'rel')
		.filter(rel => rel !== relationToParentID) // except my relation
	if (relationsAfterTarget.length) {
		DEBUG(
			`[insertBlockInRelChain] relationsAfterTarget:`,
			relationToParentID,
			blockID,
			relationsAfterTarget, /* , { relationsAfterTargetQuery, relationsOfParent } */
		)
		insertApplogsInAppDB([
			...relationsAfterTarget.map(rel => (
				{ en: rel, at: REL_DEF.after, vl: blockID ?? null }
			)),
		])
	}
	return relBuilder.en
}

export function getEditorForBlockID(blockID: string) {
	return (window.document.querySelector(`#block-${blockID} .tiptap`) as HTMLDivElement & { editor: any }).editor
}

export function ensureHomeBlockForAgent(appSettings: AppSettingsVM, appThread: ThreadWithoutFilters, agent: AgentStateClass) {
	if (appSettings.homeBlock) return
	DEBUG(`Creating home block`, { appSettings })
	const home = addBlock({
		thread: useCurrentThread(appThread),
		asChildOf: null,
		content: htmlToSerializedTiptap(`Home Block — <em>${agent.agentString}</em>`),
	})
	appSettings.update({ homeBlock: home })
	LOG(`Created home block`, { home, appSettings })
}
